import { CSSProperties, useMemo, useState } from 'react';

type Props = {
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
  disabled?: boolean;
};

const pad = (value: number) => String(value).padStart(2, '0');
const toValue = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const buttonReset: CSSProperties = {
  appearance: 'none',
  border: 0,
  boxShadow: 'none',
  margin: 0,
};

const DateTimeCalendar = ({ value, onChange, onClose, disabled = false }: Props) => {
  const [draftValue, setDraftValue] = useState(value);
  const selected = draftValue ? new Date(draftValue) : null;
  const initial = selected && !Number.isNaN(selected.getTime()) ? selected : new Date();
  const [visibleMonth, setVisibleMonth] = useState(
    () => new Date(initial.getFullYear(), initial.getMonth(), 1)
  );

  const days = useMemo(() => {
    const firstWeekday = (visibleMonth.getDay() + 6) % 7;
    const firstCell = new Date(visibleMonth);
    firstCell.setDate(1 - firstWeekday);
    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(firstCell);
      date.setDate(firstCell.getDate() + index);
      return date;
    });
  }, [visibleMonth]);

  const chooseDay = (day: Date) => {
    const next = new Date(day);
    next.setHours(selected?.getHours() ?? 9, selected?.getMinutes() ?? 0, 0, 0);
    setDraftValue(toValue(next));
  };

  const changeTime = (part: 'hour' | 'minute', amount: number) => {
    const next = selected && !Number.isNaN(selected.getTime()) ? new Date(selected) : new Date();
    next.setSeconds(0, 0);
    if (part === 'hour') next.setHours(amount);
    else next.setMinutes(amount);
    setDraftValue(toValue(next));
  };

  const chooseToday = () => {
    const next = new Date();
    next.setMinutes(Math.ceil(next.getMinutes() / 15) * 15, 0, 0);
    if (next.getMinutes() === 60) {
      next.setHours(next.getHours() + 1, 0, 0, 0);
    }
    setVisibleMonth(new Date(next.getFullYear(), next.getMonth(), 1));
    setDraftValue(toValue(next));
  };

  const confirm = () => {
    if (!draftValue) return;
    onChange(draftValue);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4 backdrop-blur-[1px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reminder-calendar-title"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h3 id="reminder-calendar-title" className="text-lg font-semibold text-gray-900">Set a reminder</h3>
            <p className="mt-0.5 text-sm text-gray-500">Choose when you would like to follow up.</p>
          </div>
          <button
            type="button"
            aria-label="Close reminder calendar"
            onClick={onClose}
            style={buttonReset}
            className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100 p-0 text-xl leading-none text-gray-500 transition hover:bg-gray-200 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-torch-primary focus:ring-offset-2"
          >
            &times;
          </button>
        </div>

        <div className="p-5">
          <div className="mb-4 rounded-xl bg-blue-50 px-4 py-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-blue-600">Selected reminder</div>
            <div className="mt-1 text-base font-semibold text-blue-950">
              {selected
                ? selected.toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                : 'Select a date below'}
            </div>
          </div>

          <div className="mb-3 flex items-center justify-between">
        <button
          type="button"
          aria-label="Previous month"
          disabled={disabled}
          onClick={() => setVisibleMonth(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() - 1, 1))}
          style={buttonReset}
          className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100 p-0 text-2xl text-gray-600 transition hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-torch-primary disabled:opacity-50"
        >
          &lsaquo;
        </button>
        <div className="text-base font-semibold text-gray-900">
          {visibleMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
        </div>
        <button
          type="button"
          aria-label="Next month"
          disabled={disabled}
          onClick={() => setVisibleMonth(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 1))}
          style={buttonReset}
          className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100 p-0 text-2xl text-gray-600 transition hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-torch-primary disabled:opacity-50"
        >
          &rsaquo;
        </button>
      </div>

      <div className="grid grid-cols-7 text-center text-xs font-semibold uppercase text-gray-400">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
          <div key={day} className="py-1">{day}</div>
        ))}
      </div>
      <div className="mt-1 grid grid-cols-7 gap-1">
        {days.map((day) => {
          const active = Boolean(selected && sameDay(day, selected));
          const outside = day.getMonth() !== visibleMonth.getMonth();
          const today = sameDay(day, new Date());
          const dayEnd = new Date(day);
          dayEnd.setHours(23, 59, 59, 999);
          const inPast = dayEnd.getTime() < Date.now();
          return (
            <button
              key={day.toISOString()}
              type="button"
              disabled={disabled || inPast}
              onClick={() => chooseDay(day)}
              style={buttonReset}
              className={`relative h-10 w-10 justify-self-center rounded-full p-0 text-sm transition focus:outline-none focus:ring-2 focus:ring-torch-primary focus:ring-offset-1 ${active ? 'bg-torch-primary font-semibold text-white' : outside ? 'bg-transparent text-gray-500 hover:bg-gray-100' : today ? 'bg-white font-semibold text-torch-primary ring-1 ring-inset ring-torch-primary hover:bg-blue-50' : 'bg-transparent text-gray-700 hover:bg-blue-50'} disabled:cursor-not-allowed disabled:bg-transparent disabled:text-gray-400 disabled:opacity-80`}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-3">
        <span className="mr-auto text-sm font-semibold text-gray-700">Reminder time</span>
        <select
          aria-label="Reminder hour"
          value={selected?.getHours() ?? 9}
          onChange={(event) => changeTime('hour', Number(event.target.value))}
          disabled={disabled}
          className="rounded-lg border border-gray-300 bg-white px-2 py-2 text-sm font-medium"
        >
          {Array.from({ length: 24 }, (_, hour) => <option key={hour} value={hour}>{pad(hour)}</option>)}
        </select>
        <span>:</span>
        <select
          aria-label="Reminder minute"
          value={selected?.getMinutes() ?? 0}
          onChange={(event) => changeTime('minute', Number(event.target.value))}
          disabled={disabled}
          className="rounded-lg border border-gray-300 bg-white px-2 py-2 text-sm font-medium"
        >
          {Array.from({ length: 60 }, (_, minute) => <option key={minute} value={minute}>{pad(minute)}</option>)}
        </select>
      </div>
        </div>

        <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 px-5 py-4">
          <button
            type="button"
            onClick={chooseToday}
            style={buttonReset}
            className="rounded-lg bg-blue-50 px-3 py-2 text-sm font-semibold text-torch-primary transition hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-torch-primary"
          >
            Today
          </button>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button
              type="button"
              onClick={confirm}
              disabled={!draftValue || disabled}
              className="rounded-lg bg-torch-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-torch-primary-dark disabled:opacity-50"
            >
              Set reminder
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DateTimeCalendar;
